import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import './os.css';
import {
  ContextTabs, StatusBadge, EmptyState, LoadingState, ErrorState, UnknownState, ActionSheet, ConfirmModal, MessageComposer, DocItem,
} from './osPrimitives';
import {
  ArrowLeft, MessageSquare, Pencil, MoreHorizontal, Phone, Mail, MapPin, Car, User, FileText,
  DollarSign, Network, Send, Upload, Users, Route, Plus, ExternalLink,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const money = (n) => (n === undefined || n === null || n === '') ? '—' : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const humanize = (s) => (s || '').toString().replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const authHeaders = () => { const t = localStorage.getItem('ace_admin_token'); return { ...(t ? { Authorization: `Bearer ${t}` } : {}) }; };
async function getJSON(path) {
  const r = await fetch(`${API}${path}`, { credentials: 'include', headers: authHeaders() });
  if (!r.ok) throw new Error(`${path} ${r.status}`);
  return r.json();
}
async function mutate(path, method = 'POST', body) {
  const r = await fetch(`${API}${path}`, { method, credentials: 'include', headers: { ...authHeaders(), ...(body ? { 'Content-Type': 'application/json' } : {}) }, body: body ? JSON.stringify(body) : undefined });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.detail || `${method} ${r.status}`);
  return j;
}
const asIso = (v) => { if (!v) return ''; if (typeof v === 'string') return v; try { return v.toISOString(); } catch { return String(v); } };
const fmtDate = (v) => { const s = asIso(v); if (!s) return '—'; const d = new Date(s); return Number.isNaN(d.getTime()) ? s : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); };
const fmtDateTime = (v) => { const s = asIso(v); if (!s) return '—'; const d = new Date(s); return Number.isNaN(d.getTime()) ? s : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); };

const TABS = [
  { id: 'overview', label: 'Overview' }, { id: 'trip', label: 'Trip' }, { id: 'client', label: 'Client' },
  { id: 'vehicle', label: 'Vehicle' }, { id: 'driver', label: 'Driver' }, { id: 'fulfillment', label: 'Fulfillment' },
  { id: 'money', label: 'Money' }, { id: 'communication', label: 'Communication' }, { id: 'documents', label: 'Documents' },
  { id: 'history', label: 'History' },
];
const STATUS_TONE = { upcoming: 'waiting', confirmed: 'ready', completed: 'ready', in_service: 'attention', cancelled: 'risk', deleted: 'risk' };
const PAY_TONE = { paid: 'ready', partial: 'attention', unpaid: 'waiting' };

export default function ServiceRecord({ mode }) {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [sheet, setSheet] = useState(false);
  const [rec, setRec] = useState({ loading: true });
  const [legacyFallback, setLegacyFallback] = useState(null);

  const load = useCallback(() => {
    setRec({ loading: true });
    getJSON(`/api/command-center/confirmed-bookings/${encodeURIComponent(bookingId)}/record`)
      .then((d) => setRec({ loading: false, d })).catch(() => setRec({ loading: false, error: true }));
  }, [bookingId]);
  useEffect(() => { load(); }, [load]);

  if (rec.loading) return <Shell onBack={() => navigate('/os/services')}><LoadingState rows={5} /></Shell>;
  if (rec.error) return <Shell onBack={() => navigate('/os/services')}><ErrorState title="Service not found" body={`No canonical booking record for “${bookingId}”.`} onRetry={load} /></Shell>;

  const { booking: b, client_id, dispatch, contracts = [], invoices = [], ledger = [], documents = [], fulfillment, fulfillment_mode, money: mny } = rec.d;
  const status = b.cc_status || 'upcoming';
  const editable = !['completed', 'cancelled', 'deleted'].includes(status);
  const openLegacy = () => navigate('/command-center?tab=quotebookings');
  const editService = () => navigate(`/os/services/${encodeURIComponent(bookingId)}/edit`);
  const openClient = () => client_id ? navigate(`/os/clients/${encodeURIComponent(client_id)}`) : toast.error('No canonical client record');
  const openFulfillment = () => navigate(`/os/network?net=fulfillment&bk=${encodeURIComponent(bookingId)}`);

  const sms = async () => {
    const to = b.client_phone;
    if (!to) { toast.error('No client phone on file'); return; }
    const message = window.prompt('Message to client:', `Hi ${b.client_name || ''}, regarding your ${b.service_type || 'service'} on ${fmtDate(b.event_date)} —`);
    if (!message) return;
    try { const r = await mutate('/api/command-center/quo/send-sms', 'POST', { to, message }); r.success ? toast.success('SMS sent') : toast.error('SMS failed'); } catch (e) { toast.error(e.message); }
  };
  const createContract = async () => { try { const r = await mutate(`/api/command-center/bookings/${bookingId}/create-contract`); toast.success(r.already_exists ? 'Contract already exists' : 'Contract created'); load(); } catch (e) { toast.error(e.message); } };
  const sendContract = async (id) => { try { await mutate(`/api/command-center/contracts/${id}/send`); toast.success('Contract sent'); } catch (e) { toast.error(e.message); } };
  const createInvoice = async () => { try { const r = await mutate(`/api/command-center/bookings/${bookingId}/create-invoice`); toast.success(r.already_exists ? 'Invoice already exists' : 'Invoice created'); load(); } catch (e) { toast.error(e.message); } };
  const sendInvoice = async (id) => { try { await mutate(`/api/command-center/invoices/${id}/send`); toast.success('Invoice sent'); } catch (e) { toast.error(e.message); } };

  const primary = editable
    ? { label: 'Edit Service', icon: Pencil, onClick: editService, testid: 'sr-primary-edit' }
    : { label: 'More Actions', icon: MoreHorizontal, onClick: () => setSheet(true), testid: 'sr-primary-more' };
  const overflow = [
    { label: 'Service Brief', onClick: () => navigate(`/os/services/${bookingId}/brief`) },
    ...(status === 'completed' ? [{ label: 'Service Recap', onClick: () => navigate(`/os/services/${bookingId}/recap`) }] : []),
    { label: 'Message Client', onClick: sms },
    { label: 'Open Client 360', onClick: openClient },
    { label: 'Open Fulfillment', onClick: openFulfillment },
    { label: 'Create Invoice', onClick: createInvoice },
    { label: 'Create Contract', onClick: createContract },
  ];

  const ctx = { b, client_id, dispatch, contracts, invoices, ledger, documents, fulfillment, fulfillment_mode, mny, status, bookingId,
    navigate, editService, openClient, openFulfillment, sms, createContract, sendContract, createInvoice, sendInvoice, load, setLegacyFallback, openLegacy };

  if (mode === 'brief') return <Shell onBack={() => navigate(`/os/services/${bookingId}`)}><BriefView {...ctx} client_id={client_id} /></Shell>;
  if (mode === 'recap') return <Shell onBack={() => navigate(`/os/services/${bookingId}`)}><RecapView {...ctx} client_id={client_id} /></Shell>;

  return (
    <Shell onBack={() => navigate('/os/services')}>
      {/* Header */}
      <div className="os-elev rounded-2xl p-5 mb-4" data-testid="service-record-header">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-[0.3em]" style={{ color: 'var(--os-red)' }}>Service · {bookingId}</p>
            <h1 className="os-display text-3xl break-words" data-testid="sr-client">{b.client_name || 'Guest'}</h1>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <StatusBadge status={STATUS_TONE[status] || 'unknown'} label={humanize(status)} testid="sr-status" />
              {b.service_type && <span className="os-chip" style={{ color: 'var(--os-text)', border: '1px solid var(--os-border)' }}>{humanize(b.service_type)}</span>}
              <StatusBadge status={PAY_TONE[mny.payment_state] || 'unknown'} label={`Pay · ${humanize(mny.payment_state)}`} testid="sr-pay" />
              <StatusBadge status={fulfillment_mode === 'ACE_DIRECT' ? 'ready' : 'attention'} label={humanize(fulfillment_mode)} testid="sr-fmode" />
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs" style={{ color: 'var(--os-muted)' }}>
              <span className="inline-flex items-center gap-1"><Route className="h-3 w-3" />{fmtDate(b.event_date)} {b.pickup_time || ''}</span>
              {(b.vehicle_name || b.vehicle || b.vehicle_class) && <span className="inline-flex items-center gap-1"><Car className="h-3 w-3" />{b.vehicle_name || b.vehicle || humanize(b.vehicle_class)}</span>}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0 w-full sm:w-auto justify-end">
            <button className="os-btn-ghost hidden sm:inline-flex" onClick={() => navigate(`/os/services/${bookingId}/brief`)} data-testid="sr-header-brief"><FileText className="h-4 w-4" /> Brief</button>
            <button className="os-btn-primary" onClick={primary.onClick} data-testid={primary.testid}><primary.icon className="h-4 w-4" /> {primary.label}</button>
            <button className="os-btn-ghost" onClick={() => setSheet(true)} data-testid="sr-more"><MoreHorizontal className="h-4 w-4" /></button>
          </div>
        </div>
      </div>

      <ContextTabs tabs={TABS} active={tab} onChange={setTab} testid="sr-tabs" />
      <div className="mt-4">
        {tab === 'overview' && <Overview {...ctx} onTab={setTab} />}
        {tab === 'trip' && <Trip {...ctx} />}
        {tab === 'client' && <ClientTab {...ctx} />}
        {tab === 'vehicle' && <Vehicle {...ctx} />}
        {tab === 'driver' && <Driver {...ctx} />}
        {tab === 'fulfillment' && <Fulfillment {...ctx} />}
        {tab === 'money' && <Money {...ctx} />}
        {tab === 'communication' && <Communication {...ctx} />}
        {tab === 'documents' && <Documents {...ctx} />}
        {tab === 'history' && <History {...ctx} />}
      </div>

      <ActionSheet open={sheet} title="Service actions" actions={overflow} onClose={() => setSheet(false)} testid="sr-action-sheet" />
      <ConfirmModal open={!!legacyFallback} title="Legacy action" body={legacyFallback || ''} confirmLabel="Open Legacy Tool" onConfirm={() => { setLegacyFallback(null); openLegacy(); }} onClose={() => setLegacyFallback(null)} testid="sr-legacy-modal" confirmTestid="sr-legacy-confirm" />
    </Shell>
  );
}

const Shell = ({ children, onBack }) => (
  <div className="os-root min-h-screen">
    <header className="sticky top-0 z-40 os-surface border-b" style={{ borderColor: 'var(--os-border)' }}>
      <div className="h-14 px-4 flex items-center gap-3 max-w-5xl mx-auto w-full">
        <button onClick={onBack} className="p-2 rounded-lg" style={{ color: 'var(--os-muted)' }} data-testid="sr-back"><ArrowLeft className="h-5 w-5" /></button>
        <p className="text-[10px] font-bold uppercase tracking-[0.3em]" style={{ color: 'var(--os-red)' }}>ACE OS · Service Record</p>
      </div>
    </header>
    <main className="max-w-5xl mx-auto w-full px-4 py-5 os-animate">{children}</main>
  </div>
);

const Card = ({ title, children, testid, action }) => (
  <div className="os-card p-4" data-testid={testid}>
    <div className="flex items-center justify-between mb-2">
      <h3 className="text-[10px] uppercase tracking-widest font-bold" style={{ color: 'var(--os-muted)' }}>{title}</h3>
      {action}
    </div>
    {children}
  </div>
);
const Row = ({ label, value, strong }) => (
  <div className="flex items-center justify-between py-1"><span className="text-xs" style={{ color: 'var(--os-muted)' }}>{label}</span>
    <span className="text-sm text-right" style={{ color: strong ? 'var(--os-red)' : 'var(--os-text)', fontWeight: strong ? 800 : 500 }}>{value ?? '—'}</span></div>
);
const HealthPill = ({ label, status, note }) => (
  <div className="os-card p-3">
    <div className="flex items-center justify-between"><span className="text-[10px] uppercase tracking-wider font-bold" style={{ color: 'var(--os-muted)' }}>{label}</span>
      <StatusBadge status={status} label={note} /></div>
  </div>
);

/* ── OVERVIEW ── */
function Overview({ b, dispatch, mny, fulfillment_mode, client_id, contracts, invoices, onTab }) {
  const hasDriver = !!(dispatch && dispatch.driver_id);
  const hasVehicle = !!(b.vehicle_name || b.vehicle || (dispatch && dispatch.vehicle));
  const health = [
    { label: 'Booking', status: b.cc_status === 'cancelled' ? 'risk' : 'ready', note: humanize(b.cc_status || 'upcoming') },
    { label: 'Payment', status: PAY_TONE[mny.payment_state] || 'unknown', note: humanize(mny.payment_state) },
    { label: 'Vehicle', status: hasVehicle ? 'ready' : 'unknown', note: hasVehicle ? 'Set' : 'TBD' },
    { label: 'Driver', status: hasDriver ? 'ready' : 'unknown', note: hasDriver ? 'Assigned' : 'Unassigned' },
    { label: 'Client', status: client_id ? 'ready' : 'unknown', note: client_id ? 'CL on file' : 'Uncatalogued' },
    { label: 'Fulfillment', status: fulfillment_mode === 'ACE_DIRECT' ? 'ready' : 'attention', note: humanize(fulfillment_mode) },
  ];
  return (
    <div data-testid="sr-overview" className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="sr-health">
        {health.map((h) => <HealthPill key={h.label} {...h} />)}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Service" testid="sr-ov-service">
          <Row label="Type" value={humanize(b.service_type)} />
          <Row label="Date / time" value={`${fmtDate(b.event_date)} ${b.pickup_time || ''}`} />
          <Row label="Route" value={`${b.pickup_location || '—'} → ${b.dropoff_location || '—'}`} />
          <Row label="Passengers" value={b.passengers ?? '—'} />
          <Row label="Vehicle / class" value={b.vehicle_name || b.vehicle || humanize(b.vehicle_class) || 'TBD'} />
        </Card>
        <Card title="Money" testid="sr-ov-money" action={<button className="text-xs" style={{ color: 'var(--os-red)' }} onClick={() => onTab('money')}>Open</button>}>
          <Row label="Total" value={money(mny.total)} strong />
          <Row label="Deposit" value={money(mny.deposit)} />
          <Row label="Paid" value={money(mny.paid)} />
          <Row label="Balance" value={money(mny.balance)} strong={mny.balance > 0} />
          <Row label="Documents" value={`${contracts.length} contract(s) · ${invoices.length} invoice(s)`} />
        </Card>
      </div>
      <Card title="Service notes" testid="sr-ov-notes">
        <p className="text-sm" style={{ color: b.notes ? 'var(--os-text)' : 'var(--os-muted)' }}>{b.notes || 'No service notes recorded.'}</p>
      </Card>
    </div>
  );
}

/* ── TRIP ── */
function Trip({ b, editService, status }) {
  const editable = !['completed', 'cancelled', 'deleted'].includes(status);
  return (
    <div data-testid="sr-trip" className="space-y-4">
      <Card title="Trip details" testid="sr-trip-details" action={editable && <button className="text-xs" style={{ color: 'var(--os-red)' }} onClick={editService} data-testid="sr-trip-edit">Edit Service</button>}>
        <Row label="Service type" value={humanize(b.service_type)} />
        <Row label="Service date" value={fmtDate(b.event_date)} />
        <Row label="Pickup time" value={b.pickup_time || '—'} />
        <Row label="Pickup" value={b.pickup_location || '—'} />
        <Row label="Drop-off" value={b.dropoff_location || '—'} />
        <Row label="Duration (hrs)" value={b.hours ?? '—'} />
        <Row label="Passengers" value={b.passengers ?? '—'} />
        {b.flight_number && <Row label="Flight" value={b.flight_number} />}
        {b.dropoff_time && <Row label="Drop-off time" value={b.dropoff_time} />}
      </Card>
      {!editable && <p className="text-xs" style={{ color: 'var(--os-muted)' }}>This service is {humanize(status)} — edit via the Legacy tool if a change is required.</p>}
    </div>
  );
}

/* ── CLIENT ── */
function ClientTab({ b, client_id, openClient, sms }) {
  const [al, setAl] = useState({ loading: !!client_id });
  useEffect(() => {
    if (!client_id) { setAl({ loading: false }); return; }
    getJSON(`/api/auto-lifestyle/profile/${encodeURIComponent(client_id)}`).then((p) => setAl({ loading: false, p })).catch(() => setAl({ loading: false }));
  }, [client_id]);
  return (
    <div data-testid="sr-client-tab" className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card title="Contact" testid="sr-client-contact" action={client_id && <button className="text-xs" style={{ color: 'var(--os-red)' }} onClick={openClient} data-testid="sr-open-client360">Open Client 360</button>}>
        <Row label="Name" value={b.client_name || 'Guest'} />
        <Row label="Phone" value={b.client_phone || '—'} />
        <Row label="Email" value={b.client_email || '—'} />
        <Row label="Canonical" value={client_id || 'Uncatalogued'} />
        <div className="flex gap-2 mt-3">
          <button className="os-btn-primary" style={{ minHeight: 36, padding: '7px 12px' }} onClick={sms} data-testid="sr-client-sms"><MessageSquare className="h-4 w-4" /> Message Client</button>
        </div>
      </Card>
      <Card title="Auto Lifestyle" testid="sr-client-al">
        {al.loading ? <LoadingState rows={2} />
          : !al.p ? <UnknownState title="No Auto Lifestyle profile" body="Derived from canonical activity once available." />
          : <>
            <StatusBadge status="ready" label={humanize((al.p.current_mode || '').replace(/_MODE$/, ''))} />
            <p className="text-xs mt-2" style={{ color: 'var(--os-muted)' }}>{(al.p.mode_reasons || []).slice(0, 3).map(humanize).join(' · ') || 'Mode derived from canonical activity.'}</p>
          </>}
      </Card>
    </div>
  );
}

/* ── VEHICLE ── */
function Vehicle({ b, dispatch, fulfillment_mode, bookingId, load, navigate }) {
  const assigned = b.assigned_vehicle_name || (dispatch && dispatch.vehicle) || b.vehicle_name || b.vehicle;
  const source = b.partner_org_id ? `Partner · ${b.partner_org_name || b.partner_org_id}` : 'ACE Direct';
  const [list, setList] = useState([]);
  const [sel, setSel] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { getJSON('/api/pricing/vehicles').then((d) => setList(d.vehicles || d || [])).catch(() => setList([])); }, []);
  const assign = async () => {
    if (!sel) { toast.error('Pick a vehicle'); return; }
    setBusy(true);
    try { await mutate(`/api/command-center/confirmed-bookings/${bookingId}/assign-vehicle`, 'POST', { vehicle_id: sel }); toast.success('Vehicle assigned'); load(); } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <div data-testid="sr-vehicle" className="space-y-4">
      <Card title="Vehicle" testid="sr-vehicle-card">
        <Row label="Requested class" value={humanize(b.vehicle_class) || b.vehicle || 'TBD'} />
        <Row label="Assigned vehicle" value={assigned || 'Unassigned / TBD'} />
        <Row label="Source" value={source} />
        {fulfillment_mode !== 'ACE_DIRECT' && <Row label="Fulfillment" value={humanize(fulfillment_mode)} />}
        <div className="flex flex-col sm:flex-row gap-2 mt-3">
          <select className="os-input flex-1" value={sel} onChange={(e) => setSel(e.target.value)} data-testid="sr-vehicle-select">
            <option value="">Select physical vehicle…</option>
            {(Array.isArray(list) ? list : []).map((v) => <option key={v.vehicle_id} value={v.vehicle_id}>{v.vehicle_name || v.vehicle_id}</option>)}
          </select>
          <button className="os-btn-primary" disabled={busy} onClick={assign} data-testid="sr-vehicle-assign"><Car className="h-4 w-4" /> {assigned ? 'Change Vehicle' : 'Assign Vehicle'}</button>
        </div>
        <p className="text-[11px] mt-2" style={{ color: 'var(--os-muted)' }}>Assigns a physical ACE vehicle — the requested commercial class is not changed. Availability is not fabricated.</p>
        {b.assigned_vehicle_id && <button className="os-btn-ghost mt-2" onClick={() => navigate(`/os/network/vehicles/${b.assigned_vehicle_id}`)} data-testid="sr-open-vehicle-record"><ExternalLink className="h-4 w-4" /> Open Vehicle Record</button>}
      </Card>
    </div>
  );
}

/* ── DRIVER ── */
function Driver({ dispatch, bookingId, load, navigate }) {
  const [list, setList] = useState([]);
  const [sel, setSel] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { getJSON('/api/command-center/drivers').then((d) => setList(d.drivers || [])).catch(() => setList([])); }, []);
  const assigned = dispatch && dispatch.driver_id;
  const assign = async () => {
    if (!sel) { toast.error('Pick a driver'); return; }
    setBusy(true);
    try { await mutate(`/api/command-center/confirmed-bookings/${bookingId}/assign-driver`, 'POST', { driver_id: sel }); toast.success('Driver assigned'); load(); } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const unassign = async () => {
    setBusy(true);
    try { await mutate(`/api/command-center/confirmed-bookings/${bookingId}/assign-driver`, 'DELETE'); toast.success('Driver unassigned'); load(); } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <div data-testid="sr-driver" className="space-y-4">
      <Card title="Driver" testid="sr-driver-card">
        {assigned ? <>
          <Row label="Driver" value={dispatch.driver_name || dispatch.driver_id} />
          <Row label="Driver ID" value={dispatch.driver_id} />
          <Row label="Phone" value={dispatch.driver_phone || '—'} />
          <Row label="Assignment" value={humanize(dispatch.status || 'assigned')} />
          <Row label="Assigned at" value={fmtDateTime(dispatch.assigned_at)} />
        </> : <UnknownState title="No driver assigned" body="This service has no canonical driver assignment yet." />}
        <div className="flex flex-col sm:flex-row gap-2 mt-3">
          <select className="os-input flex-1" value={sel} onChange={(e) => setSel(e.target.value)} data-testid="sr-driver-select">
            <option value="">Select driver…</option>
            {list.map((d) => <option key={d.driver_id} value={d.driver_id}>{d.name || d.driver_id}</option>)}
          </select>
          <button className="os-btn-primary" disabled={busy} onClick={assign} data-testid="sr-driver-assign"><Users className="h-4 w-4" /> {assigned ? 'Change Driver' : 'Assign Driver'}</button>
          {assigned && <button className="os-btn-ghost" disabled={busy} onClick={unassign} data-testid="sr-driver-unassign">Unassign</button>}
        </div>
        <p className="text-[11px] mt-2" style={{ color: 'var(--os-muted)' }}>Assignment syncs the canonical dispatch read model — the same BK, no new dispatch engine.</p>
        {assigned && <button className="os-btn-ghost mt-2" onClick={() => navigate(`/os/network/drivers/${dispatch.driver_id}`)} data-testid="sr-open-driver-record"><ExternalLink className="h-4 w-4" /> Open Driver Record</button>}
      </Card>
    </div>
  );
}

/* ── FULFILLMENT ── */
function Fulfillment({ fulfillment, fulfillment_mode, openFulfillment, navigate }) {
  const providerId = fulfillment && (fulfillment.awarded_provider_id || fulfillment.current_provider_id || fulfillment.provider_id);
  return (
    <div data-testid="sr-fulfillment" className="space-y-4">
      <Card title="Fulfillment" testid="sr-fulfillment-card" action={<button className="text-xs" style={{ color: 'var(--os-red)' }} onClick={openFulfillment} data-testid="sr-open-fulfillment">Open Fulfillment</button>}>
        <Row label="Mode" value={humanize(fulfillment_mode)} />
        {fulfillment ? <>
          <Row label="Request" value={fulfillment.request_id || fulfillment.booking_id} />
          <Row label="Status" value={humanize(fulfillment.status)} />
          {fulfillment.awarded_provider_id && <Row label="Provider" value={fulfillment.awarded_provider_name || fulfillment.awarded_provider_id} />}
          {fulfillment.farmout_cost != null && <Row label="Partner cost (internal)" value={money(fulfillment.farmout_cost)} strong />}
          {providerId && <button className="os-btn-ghost mt-2" onClick={() => navigate(`/os/network/partners/${providerId}`)} data-testid="sr-open-partner-record"><ExternalLink className="h-4 w-4" /> Open Partner Record</button>}
        </> : fulfillment_mode === 'ACE_DIRECT'
          ? <p className="text-sm" style={{ color: 'var(--os-muted)' }}>Served by ACE Direct — no sourcing request. Use Network to request a provider if needed.</p>
          : <p className="text-sm" style={{ color: 'var(--os-muted)' }}>No fulfillment request on record.</p>}
        <p className="text-[11px] mt-2" style={{ color: 'var(--os-muted)' }}>Request / Award / Reopen and recovery run through canonical P6/P7 in the Network workspace — not duplicated here.</p>
      </Card>
    </div>
  );
}

/* ── MONEY ── */
function Money({ mny, invoices, ledger, createInvoice, sendInvoice, bookingId, navigate }) {
  const [busy, setBusy] = useState(false);
  const paymentLink = async (sendSms) => {
    setBusy(true);
    try {
      const r = await mutate(`/api/command-center/confirmed-bookings/${bookingId}/payment-link`, 'POST', { origin_url: window.location.origin, send_sms: !!sendSms });
      if (r.checkout_url) {
        try { await navigator.clipboard.writeText(r.checkout_url); } catch (_) {}
        toast.success(sendSms ? (r.sms_sent ? 'Payment link sent by SMS' : 'Link created (SMS unavailable) — copied') : 'Payment link created & copied');
      }
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <div data-testid="sr-money" className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div className="os-card p-3"><p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: 'var(--os-muted)' }}>Total</p><p className="os-display text-2xl" style={{ color: 'var(--os-text)' }}>{money(mny.total)}</p></div>
        <div className="os-card p-3"><p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: 'var(--os-muted)' }}>Deposit</p><p className="os-display text-2xl" style={{ color: 'var(--os-text)' }}>{money(mny.deposit)}</p></div>
        <div className="os-card p-3"><p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: 'var(--os-muted)' }}>Paid</p><p className="os-display text-2xl" style={{ color: 'var(--os-success)' }}>{money(mny.paid)}</p></div>
        <div className="os-card p-3"><p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: 'var(--os-muted)' }}>Balance</p><p className="os-display text-2xl" style={{ color: mny.balance > 0 ? 'var(--os-warning)' : 'var(--os-text)' }}>{money(mny.balance)}</p></div>
      </div>
      <div className="flex flex-wrap gap-2">
        <button className="os-btn-primary" onClick={createInvoice} data-testid="sr-money-create-invoice"><Plus className="h-4 w-4" /> Create Invoice</button>
        <button className="os-btn-ghost" disabled={busy || mny.balance <= 0} onClick={() => paymentLink(false)} data-testid="sr-money-payment-link"><DollarSign className="h-4 w-4" /> Create Payment Link</button>
        <button className="os-btn-ghost" disabled={busy || mny.balance <= 0} onClick={() => paymentLink(true)} data-testid="sr-money-payment-sms"><Send className="h-4 w-4" /> Send Payment Link</button>
        <button className="os-btn-ghost" onClick={() => navigate('/os/business?tab=money')} data-testid="sr-money-open-business"><ExternalLink className="h-4 w-4" /> Open in Business</button>
      </div>
      {mny.balance <= 0 && <p className="text-[11px]" style={{ color: 'var(--os-success)' }} data-testid="sr-money-settled">No balance due — payment links are disabled for a settled service.</p>}
      <Card title="Invoices" testid="sr-money-invoices">
        {!invoices.length ? <EmptyState title="No invoices yet" body="Create an invoice from this service." />
          : <div className="space-y-2">{invoices.map((inv) => <div key={inv.invoice_id} className="flex items-center justify-between os-card px-4 py-2.5" data-testid={`sr-invoice-${inv.invoice_id}`}>
            <div><p className="font-semibold text-sm" style={{ color: 'var(--os-text)' }}>{inv.invoice_id}</p><p className="text-xs" style={{ color: 'var(--os-muted)' }}>{fmtDate(inv.created_at)} · {money(inv.subtotal)}</p></div>
            <div className="flex items-center gap-2"><StatusBadge status={inv.status === 'paid' ? 'ready' : 'attention'} label={humanize(inv.status)} />
              <button className="os-btn-ghost" style={{ minHeight: 32, padding: '5px 10px' }} onClick={() => sendInvoice(inv.invoice_id)} data-testid={`sr-send-invoice-${inv.invoice_id}`}><Send className="h-3.5 w-3.5" /> Send</button></div>
          </div>)}</div>}
      </Card>
      <Card title="Ledger" testid="sr-money-ledger">
        {!ledger.length ? <p className="text-sm" style={{ color: 'var(--os-muted)' }}>No ledger entries for this service.</p>
          : ledger.map((e, i) => <div key={i} className="flex items-center justify-between py-1.5 border-b" style={{ borderColor: 'var(--os-border)' }}>
            <span className="text-xs" style={{ color: 'var(--os-muted)' }}>{fmtDate(e.created_at)} · {humanize(e.entry_type)}</span>
            <span className="text-sm" style={{ color: e.entry_type === 'payment' ? 'var(--os-success)' : 'var(--os-text)' }}>{money(e.amount)}</span>
          </div>)}
        <p className="text-[11px] mt-2" style={{ color: 'var(--os-muted)' }}>Balances are computed by the backend — never in the browser.</p>
      </Card>
    </div>
  );
}

/* ── COMMUNICATION ── */
function Communication({ b, client_id, sms }) {
  const [c, setC] = useState({ loading: !!client_id });
  const [composer, setComposer] = useState(false);
  const reload = () => { if (!client_id) { setC({ loading: false }); return; } getJSON(`/api/crm/communications/${encodeURIComponent(client_id)}`).then((d) => setC({ loading: false, list: d.communications || d.logs || d || [] })).catch(() => setC({ loading: false, list: [] })); };
  useEffect(reload, [client_id]);
  const list = Array.isArray(c.list) ? c.list : [];
  const sendSMS = async (text) => { const r = await mutate('/api/command-center/quo/send-sms', 'POST', { to: b.client_phone, message: text }); if (client_id) { try { await mutate('/api/crm/communications', 'POST', { client_id, type: 'text', message: text }); } catch (_) {} } r.success ? toast.success('SMS sent') : toast.error('SMS failed'); reload(); };
  const logNote = async (type, text) => { await mutate('/api/crm/communications', 'POST', { client_id, type, message: text }); toast.success('Logged to client record'); reload(); };
  return (
    <div data-testid="sr-communication" className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <button className="os-btn-primary" onClick={() => setComposer(true)} data-testid="sr-comm-compose"><MessageSquare className="h-4 w-4" /> New Message</button>
      </div>
      <Card title="Communication history" testid="sr-comm-history">
        {!client_id ? <UnknownState title="No canonical client" body="Communications are linked to a CL-* client record." />
          : c.loading ? <LoadingState rows={3} />
          : !list.length ? <EmptyState title="No communications on record" />
          : list.slice(0, 20).map((m, i) => <div key={i} className="py-1.5 border-b" style={{ borderColor: 'var(--os-border)' }}>
            <div className="flex items-center justify-between"><span className="text-sm" style={{ color: 'var(--os-text)' }}>{humanize(m.type || m.channel || 'Note')}</span><span className="text-[11px]" style={{ color: 'var(--os-muted)' }}>{fmtDateTime(m.created_at)}</span></div>
            <p className="text-xs" style={{ color: 'var(--os-muted)' }}>{m.content || m.message || m.subject || ''}</p>
          </div>)}
      </Card>
      <MessageComposer open={composer} onClose={() => setComposer(false)} to={b.client_phone} clientId={client_id} emailVerified={false} onSendSMS={sendSMS} onLog={logNote} testid="sr-message-composer" />
    </div>
  );
}

/* ── DOCUMENTS ── */
function Documents({ bookingId, contracts, invoices, documents, createContract, sendContract, createInvoice, sendInvoice, load }) {
  const [uploading, setUploading] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('booking_ref', bookingId);
      const r = await fetch(`${API}/api/uploads/document`, { method: 'POST', credentials: 'include', headers: authHeaders(), body: fd });
      if (!r.ok) throw new Error(`Upload ${r.status}`);
      toast.success('Document uploaded'); load();
    } catch (err) { toast.error(err.message); } finally { setUploading(false); e.target.value = ''; }
  };
  return (
    <div data-testid="sr-documents" className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <button className="os-btn-primary" onClick={createContract} data-testid="sr-doc-create-contract"><FileText className="h-4 w-4" /> Create Contract</button>
        <button className="os-btn-ghost" onClick={createInvoice} data-testid="sr-doc-create-invoice"><Plus className="h-4 w-4" /> Create Invoice</button>
        <label className="os-btn-ghost cursor-pointer" data-testid="sr-doc-upload"><Upload className="h-4 w-4" /> {uploading ? 'Uploading…' : 'Upload Document'}<input type="file" className="hidden" onChange={upload} /></label>
      </div>
      <Card title="Contracts" testid="sr-doc-contracts">
        {!contracts.length ? <EmptyState title="No contracts" />
          : contracts.map((c) => <DocItem key={c.contract_id} name={c.contract_id} meta={fmtDate(c.created_at)} status={humanize(c.status)} statusTone={c.status === 'signed' ? 'ready' : 'unknown'} onSend={() => sendContract(c.contract_id)} testid={`sr-contract-${c.contract_id}`} />)}
      </Card>
      <Card title="Invoices" testid="sr-doc-invoices">
        {!invoices.length ? <EmptyState title="No invoices" />
          : invoices.map((inv) => <DocItem key={inv.invoice_id} name={inv.invoice_id} meta={money(inv.subtotal)} status={humanize(inv.status)} statusTone={inv.status === 'paid' ? 'ready' : 'attention'} onSend={() => sendInvoice(inv.invoice_id)} testid={`sr-doc-invoice-${inv.invoice_id}`} />)}
      </Card>
      <Card title="Uploaded documents" testid="sr-doc-uploads">
        {!documents.length ? <p className="text-sm" style={{ color: 'var(--os-muted)' }}>No uploaded documents.</p>
          : documents.map((d, i) => <DocItem key={i} name={d.filename || d.file_id} meta={fmtDate(d.uploaded_at)} href={`${API}/api/uploads/files/${d.file_id}`} testid={`sr-doc-upload-${i}`} />)}
      </Card>
    </div>
  );
}

/* ── HISTORY (composed, no new collection) ── */
function History({ b, ledger, contracts, invoices, dispatch, fulfillment }) {
  const events = [];
  if (b.created_at) events.push({ ts: b.created_at, title: 'Service created', note: b.booking_id });
  if (b.updated_at) events.push({ ts: b.updated_at, title: 'Service updated', note: '' });
  if (dispatch?.assigned_at) events.push({ ts: dispatch.assigned_at, title: 'Driver assigned', note: dispatch.driver_name || dispatch.driver_id });
  contracts.forEach((c) => c.created_at && events.push({ ts: c.created_at, title: 'Contract created', note: `${c.contract_id} · ${humanize(c.status)}` }));
  invoices.forEach((inv) => inv.created_at && events.push({ ts: inv.created_at, title: 'Invoice created', note: `${inv.invoice_id} · ${humanize(inv.status)}` }));
  ledger.forEach((e) => e.created_at && events.push({ ts: e.created_at, title: `Ledger ${humanize(e.entry_type)}`, note: money(e.amount) }));
  if (fulfillment?.created_at) events.push({ ts: fulfillment.created_at, title: 'Fulfillment request', note: fulfillment.request_id || '' });
  events.sort((a, z) => String(z.ts).localeCompare(String(a.ts)));
  return (
    <div data-testid="sr-history">
      <Card title="Service timeline" testid="sr-history-card">
        {!events.length ? <EmptyState title="No history yet" body="Timeline builds from booking, payment, document and fulfillment activity." />
          : events.map((e, i) => <div key={i} className="flex items-start gap-3 py-2 border-b" style={{ borderColor: 'var(--os-border)' }}>
            <span className="text-[11px] whitespace-nowrap" style={{ color: 'var(--os-muted)', minWidth: 96 }}>{fmtDateTime(e.ts)}</span>
            <div><p className="text-sm" style={{ color: 'var(--os-text)' }}>{e.title}</p>{e.note && <p className="text-xs" style={{ color: 'var(--os-muted)' }}>{e.note}</p>}</div>
          </div>)}
        <p className="text-[11px] mt-2" style={{ color: 'var(--os-muted)' }}>Composed from existing records — no separate history store. Unknown timestamps stay unknown.</p>
      </Card>
    </div>
  );
}


/* ── BRIEF (Known / Attention / Unknown — composed, no fabrication) ── */
const KAU = ({ tone }) => <span className="os-chip" style={{ fontSize: 10, color: tone === 'known' ? 'var(--os-success)' : tone === 'attention' ? 'var(--os-warning)' : 'var(--os-muted)', border: `1px solid ${tone === 'known' ? 'var(--os-success)' : tone === 'attention' ? 'var(--os-warning)' : 'var(--os-border)'}` }}>{tone === 'known' ? 'KNOWN' : tone === 'attention' ? 'ATTENTION' : 'UNKNOWN'}</span>;
const BriefRow = ({ label, value, tone }) => (
  <div className="flex items-start justify-between gap-3 py-1.5 border-b" style={{ borderColor: 'var(--os-border)' }}>
    <div className="min-w-0"><p className="text-xs" style={{ color: 'var(--os-muted)' }}>{label}</p><p className="text-sm truncate" style={{ color: 'var(--os-text)' }}>{value || '—'}</p></div>
    <KAU tone={tone} />
  </div>
);
function BriefView({ b, client_id, dispatch, mny, fulfillment_mode, fulfillment, bookingId, navigate }) {
  const [al, setAl] = useState({ loading: !!client_id });
  useEffect(() => { if (!client_id) { setAl({ loading: false }); return; } getJSON(`/api/auto-lifestyle/profile/${encodeURIComponent(client_id)}`).then((p) => setAl({ loading: false, p })).catch(() => setAl({ loading: false })); }, [client_id]);
  const known = (v) => v ? 'known' : 'unknown';
  const driver = dispatch && dispatch.driver_id;
  const vehicle = b.assigned_vehicle_name || (dispatch && dispatch.vehicle);
  const attention = [];
  if (mny.balance > 0) attention.push(`Outstanding balance ${money(mny.balance)}`);
  if (!driver) attention.push('No driver assigned');
  if (!vehicle) attention.push('No physical vehicle assigned');
  if (b.notes) attention.push('Service notes present — review');
  if (fulfillment && ['recovery', 'reopened', 'failed'].includes((fulfillment.status || '').toLowerCase())) attention.push(`Fulfillment ${humanize(fulfillment.status)}`);
  return (
    <div data-testid="sr-brief" className="space-y-4">
      <div className="os-elev rounded-2xl p-5">
        <p className="text-[10px] font-bold uppercase tracking-[0.3em]" style={{ color: 'var(--os-red)' }}>Service Brief · {bookingId}</p>
        <h1 className="os-display text-3xl">{b.client_name || 'Guest'}</h1>
        <p className="text-sm mt-1" style={{ color: 'var(--os-muted)' }}>{humanize(b.service_type)} · {fmtDate(b.event_date)} {b.pickup_time || ''}</p>
      </div>
      {attention.length > 0 && <Card title="Attention" testid="sr-brief-attention">
        {attention.map((a, i) => <div key={i} className="flex items-center gap-2 py-1"><KAU tone="attention" /><span className="text-sm" style={{ color: 'var(--os-text)' }}>{a}</span></div>)}
      </Card>}
      <Card title="Service" testid="sr-brief-service">
        <BriefRow label="Pickup" value={b.pickup_location} tone={known(b.pickup_location)} />
        <BriefRow label="Drop-off" value={b.dropoff_location} tone={known(b.dropoff_location)} />
        <BriefRow label="Time / duration" value={`${b.pickup_time || '—'} · ${b.hours ? `${b.hours} hrs` : 'duration TBD'}`} tone={known(b.pickup_time)} />
        <BriefRow label="Passengers" value={b.passengers != null ? String(b.passengers) : ''} tone={known(b.passengers != null)} />
        <BriefRow label="Requested class / assigned vehicle" value={`${humanize(b.vehicle_class) || b.vehicle || 'TBD'} · ${vehicle || 'Unassigned'}`} tone={vehicle ? 'known' : 'attention'} />
      </Card>
      <Card title="Client & account" testid="sr-brief-client" action={client_id && <button className="text-xs" style={{ color: 'var(--os-red)' }} onClick={() => navigate(`/os/clients/${client_id}`)}>Client 360</button>}>
        <BriefRow label="Contact" value={`${b.client_name || 'Guest'} · ${b.client_phone || 'no phone'}`} tone={known(b.client_phone)} />
        <BriefRow label="Canonical client" value={client_id} tone={known(client_id)} />
        <BriefRow label="Auto Lifestyle" value={al.loading ? 'Loading…' : (al.p ? humanize((al.p.current_mode || '').replace(/_MODE$/, '')) : '')} tone={al.p ? 'known' : 'unknown'} />
      </Card>
      <Card title="Operations" testid="sr-brief-ops">
        <BriefRow label="Driver" value={driver ? (dispatch.driver_name || dispatch.driver_id) : ''} tone={driver ? 'known' : 'attention'} />
        <BriefRow label="Fulfillment" value={humanize(fulfillment_mode)} tone="known" />
        <BriefRow label="Payment" value={`${humanize(mny.payment_state)} · balance ${money(mny.balance)}`} tone={mny.balance > 0 ? 'attention' : 'known'} />
      </Card>
      <p className="text-[11px]" style={{ color: 'var(--os-muted)' }}>Composed from canonical BK/CL evidence only. Nothing here is fabricated — unknowns stay unknown.</p>
    </div>
  );
}

/* ── RECAP (completed services — factual, NO_ACTION valid) ── */
function RecapView({ b, client_id, dispatch, mny, fulfillment, contracts, invoices, documents, bookingId, status }) {
  const driver = dispatch && dispatch.driver_id;
  const delivered = status === 'completed';
  const [created, setCreated] = useState({});
  useEffect(() => {
    getJSON(`/api/command-center/confirmed-bookings/${bookingId}/follow-ups`)
      .then((d) => { const m = {}; (d.tasks || []).filter((t) => ['pending', 'in_progress'].includes(t.status)).forEach((t) => { if (t.description) m[t.description] = true; }); setCreated(m); })
      .catch(() => {});
  }, [bookingId]);
  const followUps = [];
  if (mny.balance > 0) followUps.push(`Collect outstanding balance ${money(mny.balance)}`);
  if (!invoices.length) followUps.push('No invoice on record');
  if (fulfillment && ['recovery', 'reopened', 'failed'].includes((fulfillment.status || '').toLowerCase())) followUps.push(`Recovery required — fulfillment ${humanize(fulfillment.status)}`);
  const moneyComplete = mny.balance <= 0 && invoices.length > 0;
  const makeTask = async (reason) => {
    try { const r = await mutate(`/api/command-center/confirmed-bookings/${bookingId}/follow-up`, 'POST', { reason }); setCreated((c) => ({ ...c, [reason]: true })); toast.success(r.deduped ? 'Follow-up already exists' : 'Follow-up task created'); } catch (e) { toast.error(e.message); }
  };
  return (
    <div data-testid="sr-recap" className="space-y-4">
      <div className="os-elev rounded-2xl p-5">
        <p className="text-[10px] font-bold uppercase tracking-[0.3em]" style={{ color: 'var(--os-red)' }}>Service Recap · {bookingId}</p>
        <h1 className="os-display text-3xl">{b.client_name || 'Guest'}</h1>
        <div className="flex flex-wrap gap-2 mt-2">
          <StatusBadge status={delivered ? 'ready' : 'attention'} label={delivered ? 'Delivered' : humanize(status)} testid="sr-recap-delivered" />
          <StatusBadge status={moneyComplete ? 'ready' : 'attention'} label={moneyComplete ? 'Money complete' : 'Money open'} testid="sr-recap-money" />
          <StatusBadge status={followUps.length ? 'attention' : 'ready'} label={followUps.length ? `${followUps.length} follow-up(s)` : 'NO_ACTION'} testid="sr-recap-followup" />
        </div>
      </div>
      <Card title="What happened" testid="sr-recap-what">
        <Row label="Service" value={`${humanize(b.service_type)} · ${fmtDate(b.event_date)}`} />
        <Row label="Route" value={`${b.pickup_location || '—'} → ${b.dropoff_location || '—'}`} />
        <Row label="Driver" value={driver ? (dispatch.driver_name || dispatch.driver_id) : 'Not recorded'} />
        <Row label="Vehicle" value={b.assigned_vehicle_name || (dispatch && dispatch.vehicle) || 'Not recorded'} />
        <Row label="Fulfillment result" value={fulfillment ? humanize(fulfillment.status) : 'ACE Direct'} />
      </Card>
      <Card title="Money & documentation" testid="sr-recap-money-card">
        <Row label="Total / paid / balance" value={`${money(mny.total)} · ${money(mny.paid)} · ${money(mny.balance)}`} strong={mny.balance > 0} />
        <Row label="Invoices" value={invoices.length ? `${invoices.length} (${invoices.map((i) => humanize(i.status)).join(', ')})` : 'None'} />
        <Row label="Contracts" value={contracts.length ? `${contracts.length} (${contracts.map((c) => humanize(c.status)).join(', ')})` : 'None'} />
        <Row label="Documents" value={documents.length ? `${documents.length} file(s)` : 'None'} />
      </Card>
      <Card title="Follow-up" testid="sr-recap-followups">
        {!followUps.length ? <div className="flex items-center gap-2"><StatusBadge status="ready" label="NO_ACTION" /><span className="text-sm" style={{ color: 'var(--os-muted)' }}>Service delivered and closed — nothing actually needs follow-up.</span></div>
          : followUps.map((f, i) => <div key={i} className="flex items-center justify-between gap-2 py-1.5 border-b" style={{ borderColor: 'var(--os-border)' }}><div className="flex items-center gap-2 min-w-0"><StatusBadge status="attention" label="ACTION" /><span className="text-sm" style={{ color: 'var(--os-text)' }}>{f}</span></div>
            <button className="os-btn-ghost shrink-0" style={{ minHeight: 32, padding: '5px 10px' }} disabled={created[f]} onClick={() => makeTask(f)} data-testid={`recap-followup-create-${i}`}>{created[f] ? 'Created ✓' : 'Create Follow-Up'}</button></div>)}
      </Card>
      <p className="text-[11px]" style={{ color: 'var(--os-muted)' }}>Factual recap from canonical records — no speculative commentary. NO_ACTION is a valid outcome.</p>
    </div>
  );
}
